import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Hidden from '@material-ui/core/Hidden';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Checkbox from '@material-ui/core/Checkbox';
import Button from '@material-ui/core/Button';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import CountTextField from '@/components/CountTextField';

import helper from '@/helper';
import firebase from '@/services/firebase';
import useMultiFirebaseDatabaseQuery from '@/hooks/useMultiFirebaseDatabaseQuery';
import { useShoppingCartContext } from '@/contexts/useShoppingCartContext';
import Header from '@/containers/Layout/Header';
import ProgressBackdrop from '@/components/ProgressBackdrop';
import config from '@/configs/config';
import DeliveryMethodInput, { isDeliveryMethodDataComplete } from '../../components/DeliveryMethodInput';

const ShoppingCartPage = () => {
  const theme = useTheme();
  const isInDesktop = useMediaQuery(theme.breakpoints.up('sm'));

  const {
    shoppingCart,
    addPreOrderToShoppingCart,
    removePreOrderFromShoppingCart,
  } = useShoppingCartContext();
  const history = useHistory();

  const preOrderIds = React.useMemo(() => Object.keys(shoppingCart || {})
    , [ ]); //eslint-disable-line

  const { isLoading: isLoadingPreOrders, data: preOrders } = useMultiFirebaseDatabaseQuery(
    preOrderIds.map((preOrderId) => firebase.database().ref(`/${process.env.REACT_APP_MODE}/pre-order/preOrderEvents/${preOrderId}`))
  );
  const [providerPreOrderIds, setProviderPreOrderIds] = React.useState({});
  React.useEffect(() => {
    if (isLoadingPreOrders || !preOrders) return;
    const newProviderPreOrderIds = {};
    preOrderIds.forEach((preOrderId, index) => {
      const provider = preOrders[index]?.provider || '';
      newProviderPreOrderIds[provider] = {
        ...(newProviderPreOrderIds[provider] || {}),
        [preOrderId]: index,
      };
    });
    setProviderPreOrderIds(newProviderPreOrderIds);
  }, [preOrders, isLoadingPreOrders, preOrderIds]);

  const [selectedPreOrders, setSelectedPreOrders] = React.useState({});
  const [hasAgreedTerms, setHasAgreedTerms] = React.useState(false);

  const isCheckoutButtonDisabled = !hasAgreedTerms || Object.keys(providerPreOrderIds).map(
    (provider) => Object.keys(providerPreOrderIds[provider] || {}).map(
      (preOrderId) => {
        if (!shoppingCart[preOrderId]) return null;
        if (!selectedPreOrders[preOrderId]) return null;
        const index = providerPreOrderIds[provider][preOrderId];
        const preOrder = preOrders[index];
        if (!preOrder) return false;
        if (isDeliveryMethodDataComplete(shoppingCart[preOrderId], preOrder)) {
          return true;
        }
        return false;
      }
    )
  ).flat().reduce((result, current) => {
    if (current === true) return [true, result[1]];
    if (current === false) return [result[0], false];
    return result;
  }, [false, true])
    .find((d) => d === false) >= 0;

  const handleCheckout = () => {
    const checkoutData = {};
    Object.keys(providerPreOrderIds).forEach((provider) => {
      Object.keys(providerPreOrderIds[provider] || {}).forEach(
        (preOrderId) => {
          if (!shoppingCart[preOrderId]) return null;
          if (!selectedPreOrders[preOrderId]) return null;
          const index = providerPreOrderIds[provider][preOrderId];
          const preOrder = preOrders[index];
          if (!preOrder) return null;
          if (isDeliveryMethodDataComplete(shoppingCart[preOrderId], preOrder)) {
            checkoutData[preOrderId] = {
              ...preOrder,
              ...shoppingCart[preOrderId],
            };
          }
          return null;
        }
      );
    });
    history.push('/pre-orders/checkout', checkoutData);
  };

  const totalPrice = Object.keys(providerPreOrderIds).map(
    (provider) => Object.keys(providerPreOrderIds[provider] || {}).map(
      (preOrderId) => {
        if (!shoppingCart[preOrderId]) return 0;
        if (!selectedPreOrders[preOrderId]) return 0;
        const { amount } = shoppingCart[preOrderId];

        const index = providerPreOrderIds[provider][preOrderId];
        const preOrder = preOrders[index];

        if (!preOrder) return 0;
        const basicSalePrice = helper.getBasePrice(preOrder);

        return basicSalePrice * amount;
      }
    )
  ).flat().reduce((result, current) => result + current, 0);

  const isSelectingAll = React.useMemo(() => {
    const selectedAmount = Object.keys(selectedPreOrders).reduce((result, preOrderId) => {
      if (selectedPreOrders[preOrderId] && shoppingCart[preOrderId]) return result + 1;
      return result;
    }, 0);
    return selectedAmount > 0 && selectedAmount === Object.keys(shoppingCart).length;
  }, [selectedPreOrders, shoppingCart]);

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      Object.keys(providerPreOrderIds).forEach((provider) => (
        Object.keys(providerPreOrderIds[provider] || {})
          .forEach((preOrderId) => { selectedPreOrders[preOrderId] = true; })
      ));
      setSelectedPreOrders({ ...selectedPreOrders });
    } else {
      setSelectedPreOrders({});
    }
  };

  return (
    <>
      <Hidden only="xs">
        <Header />
        <Box fontWeight="bold" textAlign="center" my={5} position="relative">
          購物車清單
          <Box
            fontWeight="bold"
            style={{ cursor: 'pointer', position: 'absolute', top: 0 }}
            onClick={() => history.push('/pre-orders')}
          >
            ◀ 繼續購物
          </Box>
        </Box>

        <Box mt={5} height="50px" lineHeight="50px" borderBottom="1px solid black" borderTop="1px solid black">
          <Grid container>
            <Grid item xs={9}>
              <Box style={{ whiteSpace: 'nowrap', maxWidth: '100%' }}>
                <Checkbox
                  checked={isSelectingAll}
                  style={{ display: 'inline-block', margin: '0px 10px 0px 10px' }}
                  color="secondary"
                  onChange={handleSelectAll}
                />
                選擇所有商品
              </Box>
            </Grid>
            <Grid item xs={2}>
              <Box maxWidth="150px" ml="auto" textAlign="center">
                訂購數量
              </Box>
            </Grid>
            <Grid item xs={1}>
              <Box textAlign="center">
                總計
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Hidden>

      {
        !isLoadingPreOrders && Object.keys(providerPreOrderIds).map((provider) => {
          const preOrdersForThisProvider = providerPreOrderIds[provider] || {};
          const preOrderIdsForThisProvider = Object.keys(preOrdersForThisProvider);
          if (!preOrderIdsForThisProvider.find((preOrderId) => shoppingCart[preOrderId])) {
            return null;
          }
          return (
            <Box key={provider}>
              <Box style={{ whiteSpace: 'nowrap', maxWidth: '100%' }} mt={4} mb={1} bgcolor="rgb(242,242,242)">
                <Checkbox
                  color="secondary"
                  style={{ display: 'inline-block', verticalAlign: 'middle', margin: '0 10px 0 10px' }}
                  checked={
                    preOrderIdsForThisProvider.find((preOrderId) => (
                      !selectedPreOrders[preOrderId] && shoppingCart[preOrderId]
                    )) === undefined
                  }
                  onChange={(event) => {
                    const { checked } = event.target;
                    const newSelectedPreOrders = { ...selectedPreOrders };
                    preOrderIdsForThisProvider.forEach((preOrderId) => {
                      newSelectedPreOrders[preOrderId] = checked;
                    });
                    setSelectedPreOrders(newSelectedPreOrders);
                  }}
                />
                <span style={{ verticalAlign: 'middle' }}>{provider}</span>
              </Box>
              {
                preOrderIdsForThisProvider.map((preOrderId) => {
                  if (!shoppingCart[preOrderId]) return null;
                  const { amount } = shoppingCart[preOrderId];
                  const index = preOrdersForThisProvider[preOrderId];
                  const preOrder = preOrders[index];

                  if (!preOrder) return null;
                  const basicSalePrice = helper.getBasePrice(preOrder);

                  const checkbox = (
                    <Checkbox
                      color="secondary"
                      style={{
                        display: 'inline-block',
                        verticalAlign: 'top',
                        margin: isInDesktop ? '55px 10px 10px 10px' : '10px',
                      }}
                      checked={!!selectedPreOrders[preOrderId]}
                      onChange={() => {
                        setSelectedPreOrders({
                          ...selectedPreOrders,
                          [preOrderId]: !selectedPreOrders[preOrderId],
                        });
                      }}
                    />
                  );

                  const image = (
                    <img
                      style={{
                        objectFit: 'cover',
                        padding: isInDesktop ? '20px 10px 20px 0px' : '5px',
                        display: 'inline-block',
                        verticalAlign: 'top',
                      }}
                      width={isInDesktop ? '150px' : '100px'}
                      height={isInDesktop ? '150px' : '100px'}
                      src={`${config.RESOURCE_URI}${preOrder?.coverImage}`}
                      alt=""
                    />
                  );

                  const title = (
                    <Box
                      p={2}
                      height="100%"
                      style={{
                        display: 'inline-block',
                        verticalAlign: 'top',
                        maxWidth: isInDesktop ? 'calc(100% - 212px)' : '100%',
                        whiteSpace: 'normal',
                      }}
                    >
                      <Box fontSize="body1.fontSize" fontWeight="fontWeightBold" mb={2}>{preOrder?.title || ''}</Box>
                    </Box>
                  );

                  const deliveryMethodInput = (
                    <Box p={1} height="100%" ml={isInDesktop ? 0 : '52px'}>
                      <DeliveryMethodInput
                        preOrder={preOrder}
                        defaultValue={shoppingCart[preOrderId]}
                        onChange={(data) => {
                          addPreOrderToShoppingCart({ preOrderId, ...data });
                        }}
                      />
                    </Box>
                  );

                  const countTextField = (
                    <CountTextField
                      blackTonedStyle
                      defaultValue={amount}
                      value={amount}
                      minValue={preOrder?.minimumPurchaseAmount || 1}
                      onChange={(newAmount) => {
                        addPreOrderToShoppingCart({ preOrderId, amount: newAmount });
                      }}
                    />
                  );

                  const removeFromShoppingCart = (
                    <Typography
                      variant="subtitle2"
                      style={{ textDecoration: 'underline', textAlign: 'center', cursor: 'pointer' }}
                      onClick={() => removePreOrderFromShoppingCart([preOrderId])}
                    >
                      移出購物車
                    </Typography>
                  );

                  return (
                    <Box my={1} key={preOrderId} bgcolor="rgb(242,242,242)">
                      <Grid container>
                        <Hidden only="xs">
                          <>
                            <Grid item xs={5}>
                              {checkbox}
                              {image}
                              {title}
                            </Grid>
                            <Grid item xs={4}>
                              {deliveryMethodInput}
                            </Grid>
                            <Grid item xs={2}>
                              <Box bgcolor="rgb(233,233,233)" py={4} px={1} height="100%" maxWidth="150px" ml="auto">
                                {countTextField}
                                {removeFromShoppingCart}
                              </Box>
                            </Grid>
                            <Grid item xs={1}>
                              <Box bgcolor="rgb(233,233,233)" p={2} height="100%" style={{ position: 'relative' }}>
                                <Box style={{
                                  left: '50%',
                                  top: '50%',
                                  position: 'absolute',
                                  width: '100%',
                                  textAlign: 'center',
                                  transform: 'translate(-50%, -50%)',
                                  fontWeight: 'bold',
                                  fontSize: '14px',
                                }}
                                >
                                  {`$${(amount || 0) * (basicSalePrice || 0)}`}
                                </Box>
                              </Box>
                            </Grid>
                          </>
                        </Hidden>
                        <Hidden only="sm">
                          <>
                            <Grid item xs={12}>
                              {checkbox}
                              <Box display="inline-block" padding={1} style={{ verticalAlign: 'top' }}>
                                {image}
                                {removeFromShoppingCart}
                              </Box>
                              <Box width="calc(100% - 182px)" display="inline-block" style={{ verticalAlign: 'top' }}>
                                {title}
                                <Box px={2} maxWidth="250px">
                                  <Grid container spacing={1}>
                                    <Grid item xs={6}>
                                      {countTextField}
                                    </Grid>
                                    <Grid
                                      item
                                      xs={6}
                                      style={{
                                        color: 'rgb(186, 71, 71)', fontWeight: 'bold', fontSize: '16px', alignSelf: 'center',
                                      }}
                                    >
                                      {`$${(amount || 0) * (basicSalePrice || 0)}`}
                                    </Grid>
                                  </Grid>
                                </Box>
                              </Box>
                            </Grid>
                            <Grid item xs={12}>
                              {deliveryMethodInput}
                            </Grid>
                          </>
                        </Hidden>
                      </Grid>
                    </Box>
                  );
                })
              }
            </Box>
          );
        })
      }

      {!isLoadingPreOrders && (
        <>
          <Hidden only="xs">
            <Box width="100%" textAlign="right" mt={3} mb={1} p={2} bgcolor="rgb(242,242,242)">
              <Box fontWeight="bold" display="inline-block">商品總金額</Box>
              <Box fontWeight="bold" display="inline-block" color="rgb(168,0,0)" pl={2} pr={4}>
                $
                {totalPrice}
              </Box>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleCheckout}
                disabled={isCheckoutButtonDisabled}
              >
                <Box py={1} fontSize="body1.fontSize" fontWeight="fontWeightBold" width="180px">
                  結帳
                </Box>
              </Button>
            </Box>

            <Box display="flex" alignItems="center" justifyContent="flex-end" color="rgb(168, 0, 0)" pb={5}>
              <FormControlLabel
                value={hasAgreedTerms}
                control={<Checkbox color="secondary" />}
                label="預購時段及取貨方式不一，我已詳閱各商品頁預購說明"
                onChange={() => { setHasAgreedTerms(!hasAgreedTerms); }}
              />
            </Box>
          </Hidden>
          <Hidden only="sm">
            <Box height="110px" />
            <Box
              width="100%"
              bgcolor="rgb(249,249,249)"
              style={isInDesktop ? {} : {
                bottom: 0,
                position: 'fixed',
                margin: 0,
                zIndex: 999,
              }}
            >
              <Grid container spacing={1}>
                <Grid item xs={5} style={{ alignSelf: 'center' }}>
                  <Checkbox
                    checked={isSelectingAll}
                    color="secondary"
                    onChange={handleSelectAll}
                  />
                  全選
                </Grid>
                <Grid item xs={3} style={{ alignSelf: 'center', textAlign: 'right', fontWeight: 'bold' }}>
                  <Box>商品總金額</Box>
                  <Box color="rgb(168,0,0)">
                    $
                    {totalPrice}
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleCheckout}
                    disabled={isCheckoutButtonDisabled}
                    fullWidth
                  >
                    <Box py={1} fontSize="body1.fontSize" fontWeight="fontWeightBold">
                      結帳
                    </Box>
                  </Button>
                </Grid>
              </Grid>
              <Box color="rgb(168, 0, 0)" padding="10px">
                <FormControlLabel
                  value={hasAgreedTerms}
                  control={<Checkbox color="secondary" />}
                  label="預購時段及取貨方式不一，我已詳閱各商品頁預購說明"
                  onChange={() => { setHasAgreedTerms(!hasAgreedTerms); }}
                />
              </Box>
            </Box>
          </Hidden>
        </>
      )}
      {(isLoadingPreOrders) && <ProgressBackdrop isShow />}
    </>
  );
};

export default ShoppingCartPage;
